import { openDB, type DBSchema, type IDBPDatabase } from "idb";

// ─── Database Schema ───────────────────────────────────

interface ClawDB extends DBSchema {
  runs: {
    key: string;
    value: {
      id: string;
      status: string;
      prompt: string;
      title: string;
      model: string;
      maxIterations: number;
      maxTokens: number;
      tokensUsed: number;
      questId: string | null;
      error: string | null;
      createdAt: string;
      updatedAt: string;
    };
    indexes: { "by-status": string; "by-date": string };
  };
  messages: {
    key: string;
    value: {
      id: string;
      runId: string;
      role: string;
      content: unknown; // ContentBlock[]
      tokenCount: number;
      timestamp: string;
    };
    indexes: { "by-run": string };
  };
  memories: {
    key: string;
    value: {
      key: string;
      content: string;
      category: string;
      runId: string | null;
      createdAt: string;
      updatedAt: string;
    };
    indexes: { "by-category": string };
  };
  config: {
    key: string;
    value: {
      key: string;
      value: string;
      updatedAt: string;
    };
  };
  conversations: {
    key: string;
    value: {
      runId: string;
      turns: unknown[]; // ConversationTurn[]
      updatedAt: string;
    };
  };
  panelLayouts: {
    key: string;
    value: {
      runId: string;
      panelWidth: number;
      isOpen: boolean;
      /** Per-tile layout: collapsed state and pixel height */
      tileLayouts?: Record<string, { collapsed: boolean; height: number | null }>;
      /** Active view tab */
      activeViewId?: string;
      updatedAt: string;
    };
  };
  widgetContent: {
    key: string;
    value: {
      tileId: string;
      runId: string;
      type: string;
      title: string;
      content: string;
      createdAt: string;
      updatedAt: string;
    };
    indexes: { "by-run": string };
  };
  panelViews: {
    key: string;
    value: {
      viewId: string;
      runId: string;
      title: string;
      mode: "tiles" | "widget";
      tileIds: string[];
      icon: string;
      sortOrder: number;
      isSystem: boolean;
      isHidden: boolean;
      createdAt: string;
      updatedAt: string;
    };
    indexes: { "by-run": string };
  };
}

const DB_NAME_PREFIX = "claw-agent-db";
const DB_VERSION = 4;

// ─── User-scoped DB ───────────────────────────────────
// Each user gets their own IndexedDB to prevent cross-user data leakage.

let currentUserId: string | null = null;
const dbCache = new Map<string, Promise<IDBPDatabase<ClawDB>>>();

/**
 * Set the active userId. Call on login/logout/user-switch.
 *
 * Module state is per JS realm: the agent Web Worker loads its own copy of
 * this module, so the main thread and the worker must each be told. Go
 * through setAgentUser() in ../userScope, which does both.
 */
export function setCurrentUserId(userId: string | null): void {
  currentUserId = userId;
}

// Before DEV-239 every user of a browser shared one unsuffixed DB. Its rows
// carry no owner, so they can't be migrated to a user without guessing (and
// guessing re-creates the leak) -- delete it once instead.
const LEGACY_SHARED_DB_NAME = DB_NAME_PREFIX;
const LEGACY_CLEARED_KEY = "wexare_legacy_agent_db_cleared";

/**
 * Delete the pre-DEV-239 shared DB, once per browser. Main thread only (it
 * needs localStorage). The flag is set only after the delete settles, so a
 * failure retries on the next sign-in. A tab still holding the old DB open
 * just defers the delete until that tab closes.
 */
export async function clearLegacySharedDB(): Promise<void> {
  try {
    if (localStorage.getItem(LEGACY_CLEARED_KEY) === "true") return;
    await deleteDB(LEGACY_SHARED_DB_NAME);
    localStorage.setItem(LEGACY_CLEARED_KEY, "true");
  } catch (err) {
    console.warn("[DB] Could not delete legacy shared DB:", err);
  }
}

function getDBName(userId: string): string {
  return `${DB_NAME_PREFIX}-${userId}`;
}

/**
 * Get or create the IndexedDB database for the current user.
 * Safe to call multiple times — returns the same promise per userId.
 * Rejects if no user is set (call setCurrentUserId first).
 */
export function getDB(): Promise<IDBPDatabase<ClawDB>> {
  if (!currentUserId) {
    return Promise.reject(
      new Error("[DB] No authenticated user — cannot open IndexedDB. Call setCurrentUserId() first."),
    );
  }
  const userId = currentUserId;
  let promise = dbCache.get(userId);
  if (!promise) {
    promise = openDBWithFallback(userId);
    dbCache.set(userId, promise);
  }
  return promise;
}

/** Close all cached DB connections (call on logout). */
export async function closeAllDBs(): Promise<void> {
  // Snapshot and clear synchronously: a getDB() for the next user that lands
  // while we await below must get a fresh entry, not one this loop closes.
  const pending = [...dbCache.values()];
  dbCache.clear();
  await Promise.allSettled(pending.map(async (promise) => (await promise).close()));
}

async function openDBWithFallback(userId: string): Promise<IDBPDatabase<ClawDB>> {
  const dbName = getDBName(userId);
  try {
    // Race the DB open against a timeout — if an upgrade is blocked by
    // a stale tab/connection, openDB will hang indefinitely.
    const db = await Promise.race([
      openDBInternal(dbName),
      rejectAfter(3000, "IndexedDB open timed out (upgrade likely blocked by another tab)"),
    ]);

    // Verify the DB has all expected stores (handles case where upgrade was
    // blocked by a stale tab/HMR and we got an older version)
    if (db.version < DB_VERSION || !db.objectStoreNames.contains("panelViews")) {
      console.warn("[DB] Stale DB version", db.version, "expected", DB_VERSION, "— closing and retrying");
      db.close();
      dbCache.delete(userId);
      // Brief delay to let the close propagate, then delete and recreate
      await new Promise((r) => setTimeout(r, 100));
      await deleteDB(dbName);
      return openDBInternal(dbName);
    }

    return db;
  } catch (err) {
    console.warn("[DB] Failed to open, deleting and retrying:", err);
    // Delete the old DB and try again — session history is non-critical
    await deleteDB(dbName);
    dbCache.delete(userId);
    return openDBInternal(dbName);
  }
}

function openDBInternal(dbName: string): Promise<IDBPDatabase<ClawDB>> {
  return openDB<ClawDB>(dbName, DB_VERSION, {
    upgrade(db, oldVersion) {
      console.log("[DB] upgrading from version", oldVersion, "to", DB_VERSION);
      if (oldVersion < 1) {
        const runStore = db.createObjectStore("runs", { keyPath: "id" });
        runStore.createIndex("by-status", "status");
        runStore.createIndex("by-date", "createdAt");

        const msgStore = db.createObjectStore("messages", { keyPath: "id" });
        msgStore.createIndex("by-run", "runId");

        const memStore = db.createObjectStore("memories", { keyPath: "key" });
        memStore.createIndex("by-category", "category");

        db.createObjectStore("config", { keyPath: "key" });
      }

      if (oldVersion < 2) {
        db.createObjectStore("conversations", { keyPath: "runId" });
      }

      if (oldVersion < 3) {
        db.createObjectStore("panelLayouts", { keyPath: "runId" });
        const widgetStore = db.createObjectStore("widgetContent", { keyPath: "tileId" });
        widgetStore.createIndex("by-run", "runId");
      }

      if (oldVersion < 4) {
        const viewStore = db.createObjectStore("panelViews", { keyPath: "viewId" });
        viewStore.createIndex("by-run", "runId");
      }
    },
    blocked(currentVersion, blockedVersion) {
      console.warn("[DB] Upgrade blocked. Current:", currentVersion, "Target:", blockedVersion);
    },
    blocking(currentVersion, blockedVersion) {
      console.warn("[DB] This connection is blocking version", blockedVersion, "from", currentVersion);
    },
    terminated() {
      console.warn("[DB] Connection terminated unexpectedly");
      // Clear cached promises so next getDB() call re-opens
      dbCache.clear();
    },
  });
}

function rejectAfter(ms: number, message: string): Promise<never> {
  return new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms));
}

async function deleteDB(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => {
      console.warn("[DB] deleteDatabase blocked — proceeding anyway");
      resolve();
    };
  });
}

export type { ClawDB };
